import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  cancelMainUserSubscription,
  deleteSubscriptionUser,
  getSubscriptionActiveDataCount,
  getSubscriptionData,
  getSubscriptionDetailByUserId,
  getSubscriptionDetails,
  revokeSubscription,
} from "../../../../Services/subscriptionService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { setUser } from "../../../../Redux/Slice/authSlice";

const MySubscriptionPage = () => {
  const currentLanguage = useSelector(
    (state) => state?.global?.current_language
  );
  const [isLoading, setIsLoading] = useState(false);
  const [customerSubscriptionDetails, setCustomerSubscriptionDetails] =
    useState({});
  const [pricingData, setPricingData] = useState([]);
  const customerId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const loggedInUserDetails = useSelector((state) => state?.auth?.loggedInUser);
  const [subscriptionPlan, setSubscriptionPlan] = useState({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [userSubscribedCount, setUserSubscriberCount] = useState(0);
  const [userSubscriberData, setUserSubscriberData] = useState([]);
  const [currentEvent, setCurrentEvent] = useState("cancel_subscription");
  const [currentRevokeId, setCurrentRevokeId] = useState("");
  const [currentIndex] = useState("");
  const userId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getSubscriptionDataAndCount = useCallback(async () => {
    if (!customerId) return;

    try {
      const [dataResponse, countResponse] = await Promise.all([
        getSubscriptionData(customerId),
        getSubscriptionActiveDataCount(customerId),
      ]);

      const userDataResponse = dataResponse?.data;
      const filterUserDataResponse = userDataResponse?.filter(
        (item) => !item?.isDeleted && item?.isInvitedUser
      );
      setUserSubscriberData(filterUserDataResponse);
      setUserSubscriberCount(countResponse?.data);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    }
  }, [customerId]);

  const getUserSubscriptionDetail = useCallback(async (customerId) => {
    if (!customerId) return;

    try {
      setIsLoading(true);
      const response = await getSubscriptionDetailByUserId(customerId);
      if (response.data !== null && response?.data?.isActive) {
        setCustomerSubscriptionDetails(response?.data);
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getAllSubscriptionPlan = useCallback(async () => {
    try {
      const response = await getSubscriptionDetails();
      setPricingData(response?.data);
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    }
  }, []);

  useEffect(() => {
    if (!customerId) return;

    getAllSubscriptionPlan();
    getUserSubscriptionDetail(customerId);
    getSubscriptionDataAndCount();
  }, [
    customerId,
    getAllSubscriptionPlan,
    getUserSubscriptionDetail,
    getSubscriptionDataAndCount,
  ]);

  useEffect(() => {
    if (customerSubscriptionDetails && pricingData?.length > 0) {
      const planDetails = pricingData?.find(
        (plan) =>
          plan?.subscriptionType === Number(customerSubscriptionDetails.plan)
      );
      setSubscriptionPlan(planDetails);
    }
  }, [customerSubscriptionDetails, pricingData]);

  const getDateDetails = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const remainingTime = (endTime) => {
    if (!endTime) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

    const endDate = new Date(endTime);
    const now = new Date();
    const difference = endDate - now;

    if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  const handleConfirmation = async (isConfirmation) => {
    if (!isConfirmation) {
      setShowConfirmationModal(false);
      return;
    }

    setIsLoading(true);

    try {
      let response;

      switch (currentEvent) {
        case "cancel_subscription":
          response = await revokeSubscription(currentRevokeId, {
            subscriptionRevokeStatus: true,
          });
          if (response.success) {
            toast.success("Subscription Cancelled Successfully");
            setCurrentEvent("continue_subscription");
          }
          break;

        case "cancel_main_user_subscription":
          response = await cancelMainUserSubscription(userId);
          if (response.success) {
            dispatch(
              setUser({ ...loggedInUserDetails, isSubscription: false })
            );
            toast.success("Subscription Cancelled Successfully");
            setCurrentEvent("continue_subscription");
            navigate("/");
          }
          break;

        case "continue_subscription":
          response = await revokeSubscription(currentRevokeId, {
            subscriptionRevokeStatus: false,
          });
          if (response.success) {
            toast.success("Subscription Continued Successfully");
            setCurrentEvent("cancel_subscription");
          }
          break;

        case "delete_subscription":
          response = await deleteSubscriptionUser(currentRevokeId);
          if (response.success) {
            toast.success("Subscription Deleted Successfully");
          }
          break;

        default:
          break;
      }

      if (!response?.success) {
        toast.error("Something went wrong");
      } else {
        await getSubscriptionDataAndCount();
        await getUserSubscriptionDetail(customerId);
      }
    } catch (error) {
      console.error("Error handling confirmation:", error);
      toast.error("An error occurred");
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  const getUsagePercentage = () => {
    const maxUsers = subscriptionPlan?.users || 1;
    return Math.min((userSubscribedCount / maxUsers) * 100, 100);
  };

  const getStatusColor = () => {
    const remaining = remainingTime(customerSubscriptionDetails?.endTime);
    if (remaining.days <= 7) return "text-red-600 bg-red-50";
    if (remaining.days <= 30) return "text-yellow-600 bg-yellow-50";
    return "text-green-600 bg-green-50";
  };

  const getProgressColor = () => {
    const percentage = getUsagePercentage();
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 70) return "bg-yellow-500";
    return "bg-blue-500";
  };

  if (isLoading && !customerSubscriptionDetails?._id) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            My Subscription
          </h1>
          <p className="text-gray-600">
            Manage your subscription plan and users
          </p>
        </div>

        {/* Main Subscription Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 mb-8">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">
                  {subscriptionPlan?.mainTitle || "Premium Plan"}
                </h2>
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()}`}
                >
                  <div className="w-2 h-2 bg-current rounded-full mr-2 animate-pulse"></div>
                  Active Subscription
                </div>
              </div>
              <button
                onClick={() => {
                  setCurrentEvent("cancel_main_user_subscription");
                  setCurrentRevokeId(customerSubscriptionDetails?._id);
                  setShowConfirmationModal(true);
                }}
                disabled={isLoading}
                className="group bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg
                  className="w-5 h-5 group-hover:rotate-12 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Cancel Subscription
              </button>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Subscription Period */}
              <div className="group bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mr-4">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Subscription Period
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">
                      Start Date
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {getDateDetails(customerSubscriptionDetails?.startTime)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">End Date</span>
                    <span className="text-gray-900 font-semibold">
                      {getDateDetails(customerSubscriptionDetails?.endTime)}
                    </span>
                  </div>

                  <div className="pt-4 border-t border-blue-200">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-1">
                        {
                          remainingTime(customerSubscriptionDetails?.endTime)
                            ?.days
                        }
                      </div>
                      <div className="text-sm text-gray-600 font-medium">
                        days remaining
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Management */}
              <div className="group bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mr-4">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    User Management
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">
                      Active Users
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      {userSubscribedCount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">
                      Total Capacity
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {subscriptionPlan?.users}
                    </span>
                  </div>

                  <div className="pt-2">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Usage</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {getUsagePercentage().toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressColor()} rounded-full transition-all duration-500 ease-out`}
                        style={{ width: `${getUsagePercentage()}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Features */}
              <div className="group bg-gradient-to-br from-purple-50 to-indigo-100 rounded-2xl p-6 border border-purple-200 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mr-4">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Plan Features
                  </h3>
                </div>

                <div className="space-y-3">
                  {subscriptionPlan?.features
                    ?.slice(0, 5)
                    ?.map((feature, index) => (
                      <div key={index} className="flex items-center group/item">
                        <div
                          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                            feature.isApplicable
                              ? "bg-green-100"
                              : "bg-gray-100"
                          }`}
                        >
                          {feature.isApplicable ? (
                            <svg
                              className="w-4 h-4 text-green-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            feature.isApplicable
                              ? "text-gray-900"
                              : "text-gray-500"
                          } group-hover/item:text-purple-600 transition-colors`}
                        >
                          {currentLanguage === "no"
                            ? feature.featureNameNo
                            : feature.featureName}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            {
              label: "Days Used",
              value: Math.floor(
                (new Date() -
                  new Date(customerSubscriptionDetails?.startTime)) /
                  (1000 * 60 * 60 * 24)
              ),
              color: "blue",
            },
            {
              label: "Usage Rate",
              value: `${getUsagePercentage().toFixed(0)}%`,
              color: "green",
            },
            {
              label: "Plan Type",
              value: subscriptionPlan?.mainTitle?.split(" ")[0] || "Premium",
              color: "purple",
            },
            { label: "Status", value: "Active", color: "emerald" },
          ].map((stat, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
            >
              <div
                className={`w-12 h-12 bg-${stat.color}-100 rounded-xl flex items-center justify-center mb-4`}
              >
                <div
                  className={`w-6 h-6 bg-${stat.color}-500 rounded-full`}
                ></div>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {stat.value}
              </div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Confirmation Modal */}
        {showConfirmationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-300">
              <div className="p-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>

                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                  Confirm Action
                </h3>

                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to{" "}
                  {currentEvent === "delete_subscription"
                    ? "remove this user"
                    : currentEvent === "cancel_main_user_subscription"
                    ? "cancel your subscription"
                    : userSubscriberData?.[currentIndex]?.isActive
                    ? "deactivate this user"
                    : "activate this user"}
                  ? This action cannot be undone.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleConfirmation(false)}
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConfirmation(true)}
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </>
                    ) : (
                      "Confirm"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubscriptionPage;
